import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getCustomer,
  updateCustomer,
  deleteCustomer,
  uploadCustomerLogo,
  deleteCustomerLogo,
  addContact,
  deleteContact,
  addCrmSession,
  deleteCrmSession,
  createDeal,
  listMembers,
  type CustomerKind,
  type CustomerStatus,
  type CrmSessionEntry,
} from "@/lib/api";
import { CUSTOMER_STATUS_META, STAGE_COLOR, STAGE_COLUMNS, fmtMoney } from "@/lib/crm";
import { EditableLogo } from "@/components/EntityLogo";

const inputCls =
  "w-full rounded-token-md border border-border bg-bg px-3 py-2 text-sm text-text outline-none focus:border-accent";
const labelCls = "mb-1 block text-xs font-medium text-text-muted";

export function CustomerModal({
  customerId,
  onClose,
}: {
  customerId: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data: customer } = useQuery({
    queryKey: ["customer", customerId],
    queryFn: () => getCustomer(customerId),
  });
  const { data: members } = useQuery({ queryKey: ["members"], queryFn: listMembers });

  const [name, setName] = useState("");
  const [status, setStatus] = useState<CustomerStatus>("lead");
  const [kind, setKind] = useState<CustomerKind>("company");
  const [website, setWebsite] = useState("");
  const [industry, setIndustry] = useState("");
  const [notes, setNotes] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (customer) {
      setName(customer.name);
      setStatus(customer.status);
      setKind(customer.kind);
      setWebsite(customer.website ?? "");
      setIndustry(customer.industry ?? "");
      setNotes(customer.notes ?? "");
      setOwnerId(customer.ownerId ?? "");
      setDirty(false);
    }
  }, [customer]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["customer", customerId] });
    qc.invalidateQueries({ queryKey: ["customers"] });
    qc.invalidateQueries({ queryKey: ["deals"] });
  };

  const save = useMutation({
    mutationFn: () =>
      updateCustomer(customerId, {
        name,
        status,
        kind,
        website: website || null,
        industry: industry || null,
        notes: notes || null,
        ownerId: ownerId || null,
      }),
    onSuccess: () => {
      invalidate();
      setDirty(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 1500);
    },
  });

  const remove = useMutation({
    mutationFn: () => deleteCustomer(customerId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["customers"] });
      qc.invalidateQueries({ queryKey: ["deals"] });
      onClose();
    },
  });

  const logoUpload = useMutation({
    mutationFn: (file: File) => uploadCustomerLogo(customerId, file),
    onSuccess: invalidate,
  });
  const logoRemove = useMutation({
    mutationFn: () => deleteCustomerLogo(customerId),
    onSuccess: invalidate,
  });

  const mark = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setDirty(true);
  };

  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const modal = (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4 backdrop-blur-[2px] sm:p-8"
      onClick={onClose}
    >
      <div
        className="chat-fade flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-token-lg border border-border bg-bg-elevated shadow-token-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* header */}
        <div className="flex shrink-0 items-center gap-3 border-b border-border bg-bg-card px-6 py-3.5">
          <span className="text-sm text-text-muted">Customer</span>
          <span
            className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold"
            style={{
              background: `${CUSTOMER_STATUS_META[status].color}1a`,
              color: CUSTOMER_STATUS_META[status].color,
              border: `1px solid ${CUSTOMER_STATUS_META[status].color}40`,
            }}
          >
            <span
              className="h-1.5 w-1.5 rounded-full"
              style={{ background: CUSTOMER_STATUS_META[status].color }}
            />
            {CUSTOMER_STATUS_META[status].label}
          </span>
          <span className="flex-1" />
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                if (
                  confirm(
                    "Delete this customer? Their contacts and deals go too. This cannot be undone.",
                  )
                )
                  remove.mutate();
              }}
              className="rounded-md px-2 py-1 text-xs text-danger hover:bg-danger/10"
            >
              <i className="fa-solid fa-trash mr-1" />
              Delete
            </button>
            <button
              onClick={onClose}
              className="grid h-7 w-7 place-items-center rounded-md text-text-muted hover:bg-bg-card-hover hover:text-text-heading"
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
        </div>

        {!customer ? (
          <div className="grid min-h-[16rem] flex-1 place-items-center text-text-muted">
            <i className="fa-solid fa-circle-notch fa-spin text-xl" />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-y-auto">
            <div className="grid gap-8 p-6 md:grid-cols-[minmax(0,1fr)_240px]">
              {/* left: identity + notes + contacts + deals */}
              <div className="min-w-0">
                <div className="mb-5 flex items-center gap-3">
                  <EditableLogo
                    name={customer.name}
                    url={customer.logoUrl}
                    size={52}
                    fallbackIcon={kind === "individual" ? "fa-user" : "fa-building"}
                    uploading={logoUpload.isPending}
                    onUpload={(f) => logoUpload.mutate(f)}
                    onRemove={() => logoRemove.mutate()}
                  />
                  <input
                    className="w-full min-w-0 flex-1 rounded-token-md border border-transparent bg-transparent px-2 py-1 font-display text-2xl font-semibold leading-snug text-text-heading outline-none transition-colors hover:border-border focus:border-accent"
                    value={name}
                    onChange={(e) => mark(setName)(e.target.value)}
                  />
                </div>

                <label className={labelCls}>
                  <i className="fa-solid fa-align-left mr-1.5 text-[0.6rem]" />
                  Notes
                </label>
                <textarea
                  className={`${inputCls} mb-6`}
                  rows={4}
                  placeholder="Context the team should know…"
                  value={notes}
                  onChange={(e) => mark(setNotes)(e.target.value)}
                />

                <SessionsSection
                  customerId={customerId}
                  sessions={customer.sessions}
                  onChanged={invalidate}
                />

                <ContactsSection
                  customerId={customerId}
                  contacts={customer.contacts}
                  onChanged={invalidate}
                />

                <DealsSection
                  customerId={customerId}
                  deals={customer.deals}
                  onChanged={invalidate}
                />
              </div>

              {/* right: details panel */}
              <aside className="h-fit rounded-token-md border border-border bg-bg-card p-4 md:sticky md:top-0">
                <p className="mb-3 text-[0.6rem] font-semibold uppercase tracking-wider text-text-muted">
                  Details
                </p>
                <label className={labelCls}>Type</label>
                <select
                  className={`${inputCls} mb-3`}
                  value={kind}
                  onChange={(e) => mark(setKind)(e.target.value as CustomerKind)}
                >
                  <option value="company">Company</option>
                  <option value="individual">Individual</option>
                </select>

                <label className={labelCls}>Status</label>
                <select
                  className={`${inputCls} mb-3`}
                  value={status}
                  onChange={(e) => mark(setStatus)(e.target.value as CustomerStatus)}
                >
                  {(Object.keys(CUSTOMER_STATUS_META) as CustomerStatus[]).map((s) => (
                    <option key={s} value={s}>
                      {CUSTOMER_STATUS_META[s].label}
                    </option>
                  ))}
                </select>

                <label className={labelCls}>Owner</label>
                <select
                  className={`${inputCls} mb-3`}
                  value={ownerId}
                  onChange={(e) => mark(setOwnerId)(e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {members?.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>

                <label className={labelCls}>Industry</label>
                <input
                  className={`${inputCls} mb-3`}
                  value={industry}
                  placeholder="e.g. SaaS"
                  onChange={(e) => mark(setIndustry)(e.target.value)}
                />

                <label className={labelCls}>Website</label>
                <input
                  className={inputCls}
                  value={website}
                  placeholder="https://…"
                  onChange={(e) => mark(setWebsite)(e.target.value)}
                />
                <p className="mt-3 border-t border-border-light pt-3 text-[11px] text-text-muted">
                  Added {new Date(customer.createdAt).toLocaleDateString()}
                </p>
              </aside>
            </div>
          </div>
        )}

        {/* footer save bar */}
        {customer && (
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border bg-bg-card px-6 py-3">
            {saved && (
              <span className="text-xs text-accent">
                <i className="fa-solid fa-check mr-1" />
                Saved
              </span>
            )}
            {dirty && !saved && <span className="text-xs text-text-muted">Unsaved changes</span>}
            <button
              onClick={() => save.mutate()}
              disabled={!dirty || save.isPending}
              className="rounded-token-md bg-accent text-on-accent px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ color: "var(--color-on-accent)" }}
            >
              {save.isPending ? "Saving…" : "Save changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

/** Interaction log: dated notes on how each call/meeting went. */
function SessionsSection({
  customerId,
  sessions,
  onChanged,
}: {
  customerId: string;
  sessions: CrmSessionEntry[];
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [description, setDescription] = useState("");

  const add = useMutation({
    mutationFn: () => addCrmSession(customerId, { date, description }),
    onSuccess: () => {
      setDescription("");
      setDate(new Date().toISOString().slice(0, 10));
      setAdding(false);
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteCrmSession(id),
    onSuccess: onChanged,
  });

  return (
    <div className="mb-5">
      <div className="mb-2 flex items-center justify-between">
        <label className={labelCls}>
          <i className="fa-solid fa-comments mr-1.5 text-[0.6rem]" />
          Sessions
          {sessions.length > 0 && (
            <span className="ml-1.5 rounded-full bg-bg-tertiary px-1.5 text-[10px] text-text-muted">
              {sessions.length}
            </span>
          )}
        </label>
        <button
          onClick={() => setAdding((v) => !v)}
          className="text-xs font-medium text-accent hover:underline"
        >
          {adding ? "Cancel" : "+ Log session"}
        </button>
      </div>

      {adding && (
        <form
          className="mb-2 rounded-token-md border border-border bg-bg-card p-2.5"
          onSubmit={(e) => {
            e.preventDefault();
            if (description.trim()) add.mutate();
          }}
        >
          <div className="mb-2 flex items-center gap-2">
            <input
              type="date"
              className="rounded-token-md border border-border bg-bg px-2 py-1.5 text-xs text-text outline-none focus:border-accent"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              required
            />
            <span className="text-xs text-text-muted">How did it go?</span>
          </div>
          <textarea
            className={inputCls}
            rows={3}
            placeholder="Call with Jane — walked through the launch video brief, they want a revised quote by Friday…"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            autoFocus
            required
          />
          <div className="mt-2 flex justify-end">
            <button
              type="submit"
              disabled={add.isPending || !description.trim()}
              className="rounded-token-md bg-accent text-on-accent px-3 py-1.5 text-xs font-semibold transition-opacity hover:opacity-90 disabled:opacity-50"
              style={{ color: "var(--color-on-accent)" }}
            >
              {add.isPending ? "Saving…" : "Save session"}
            </button>
          </div>
        </form>
      )}

      {sessions.length === 0 && !adding ? (
        <p className="text-xs text-text-muted">
          No sessions logged yet — keep a note after each call or meeting.
        </p>
      ) : (
        <ul className="space-y-2">
          {sessions.map((sess) => (
            <li
              key={sess.id}
              className="group rounded-token-md border border-border bg-bg-card p-2.5"
            >
              <div className="mb-1 flex items-center gap-2">
                <span className="rounded bg-accent-muted px-1.5 py-0.5 text-[10px] font-semibold text-accent">
                  {new Date(sess.date).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </span>
                <span className="flex-1" />
                <button
                  onClick={() => {
                    if (confirm("Delete this session note?")) remove.mutate(sess.id);
                  }}
                  title="Delete session"
                  className="grid h-5 w-5 place-items-center rounded text-[10px] text-text-muted opacity-0 transition-opacity hover:text-danger group-hover:opacity-100"
                >
                  <i className="fa-solid fa-trash" />
                </button>
              </div>
              <p className="whitespace-pre-wrap text-xs leading-relaxed text-text">
                {sess.description}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ContactsSection({
  customerId,
  contacts,
  onChanged,
}: {
  customerId: string;
  contacts: { id: string; name: string; email: string | null; role: string | null }[];
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");

  const add = useMutation({
    mutationFn: () =>
      addContact(customerId, {
        name,
        email: email || undefined,
        role: role || undefined,
      }),
    onSuccess: () => {
      setName("");
      setEmail("");
      setRole("");
      setAdding(false);
      onChanged();
    },
  });
  const remove = useMutation({
    mutationFn: (id: string) => deleteContact(id),
    onSuccess: onChanged,
  });

  return (
    <div className="mb-5">
      <div className="mb-2 flex items-center justify-between">
        <label className={labelCls}>
          <i className="fa-solid fa-address-book mr-1.5 text-[0.6rem]" />
          Contacts
          {contacts.length > 0 && (
            <span className="ml-1.5 rounded-full bg-bg-tertiary px-1.5 text-[10px] text-text-muted">
              {contacts.length}
            </span>
          )}
        </label>
        <button
          onClick={() => setAdding((v) => !v)}
          className="text-xs font-medium text-accent hover:underline"
        >
          {adding ? "Cancel" : "+ Add contact"}
        </button>
      </div>

      {adding && (
        <form
          className="mb-2 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) add.mutate();
          }}
        >
          <input
            className={inputCls}
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            required
          />
          <input
            className={inputCls}
            placeholder="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <input
            className={inputCls}
            placeholder="Role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
          />
          <button
            type="submit"
            disabled={add.isPending}
            className="shrink-0 rounded-token-md bg-accent text-on-accent px-3 text-sm font-semibold disabled:opacity-50"
            style={{ color: "var(--color-on-accent)" }}
          >
            <i className="fa-solid fa-check" />
          </button>
        </form>
      )}

      {contacts.length === 0 && !adding ? (
        <p className="text-xs text-text-muted">No contacts yet.</p>
      ) : (
        <ul className="divide-y divide-border-light rounded-token-md border border-border bg-bg-card">
          {contacts.map((c) => (
            <li key={c.id} className="group flex items-center gap-3 px-3 py-2">
              <div className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium text-text-heading">
                  {c.name}
                  {c.role && (
                    <span className="ml-2 font-normal text-text-muted">{c.role}</span>
                  )}
                </span>
                {c.email && (
                  <a
                    href={`mailto:${c.email}`}
                    className="truncate text-xs text-text-secondary hover:text-accent"
                  >
                    {c.email}
                  </a>
                )}
              </div>
              <button
                onClick={() => remove.mutate(c.id)}
                title="Remove contact"
                className="grid h-6 w-6 shrink-0 place-items-center rounded text-xs text-text-muted opacity-0 transition-opacity hover:text-danger group-hover:opacity-100"
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DealsSection({
  customerId,
  deals,
  onChanged,
}: {
  customerId: string;
  deals: { id: string; title: string; value: number | null; stage: string }[];
  onChanged: () => void;
}) {
  const [adding, setAdding] = useState(false);
  const [title, setTitle] = useState("");
  const [value, setValue] = useState("");

  const add = useMutation({
    mutationFn: () =>
      createDeal({
        customerId,
        title,
        value: value ? Number(value) : undefined,
      }),
    onSuccess: () => {
      setTitle("");
      setValue("");
      setAdding(false);
      onChanged();
    },
  });

  const stageLabel = (s: string) =>
    STAGE_COLUMNS.find((c) => c.stage === s)?.label ?? s;

  return (
    <div className="mb-4">
      <div className="mb-2 flex items-center justify-between">
        <label className={labelCls}>
          <i className="fa-solid fa-filter-circle-dollar mr-1.5 text-[0.6rem]" />
          Deals
          {deals.length > 0 && (
            <span className="ml-1.5 rounded-full bg-bg-tertiary px-1.5 text-[10px] text-text-muted">
              {deals.length}
            </span>
          )}
        </label>
        <button
          onClick={() => setAdding((v) => !v)}
          className="text-xs font-medium text-accent hover:underline"
        >
          {adding ? "Cancel" : "+ New deal"}
        </button>
      </div>

      {adding && (
        <form
          className="mb-2 flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (title.trim()) add.mutate();
          }}
        >
          <input
            className={inputCls}
            placeholder="Deal title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            autoFocus
            required
          />
          <input
            className={`${inputCls} w-28 flex-none`}
            placeholder="£ value"
            type="number"
            min="0"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <button
            type="submit"
            disabled={add.isPending}
            className="shrink-0 rounded-token-md bg-accent text-on-accent px-3 text-sm font-semibold disabled:opacity-50"
            style={{ color: "var(--color-on-accent)" }}
          >
            <i className="fa-solid fa-check" />
          </button>
        </form>
      )}

      {deals.length === 0 && !adding ? (
        <p className="text-xs text-text-muted">
          No deals yet — new deals appear on the Pipeline board.
        </p>
      ) : (
        <ul className="divide-y divide-border-light rounded-token-md border border-border bg-bg-card">
          {deals.map((d) => (
            <li key={d.id} className="flex items-center gap-3 px-3 py-2">
              <span
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: STAGE_COLOR[d.stage as keyof typeof STAGE_COLOR] }}
                title={stageLabel(d.stage)}
              />
              <span className="min-w-0 flex-1 truncate text-sm text-text-heading">
                {d.title}
              </span>
              <span className="text-xs text-text-muted">{stageLabel(d.stage)}</span>
              <span className="w-20 text-right text-xs font-medium text-text-secondary">
                {fmtMoney(d.value)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
